"use client";

/**
 * fund-returns-table.tsx
 *
 * Clean tabular view of all available return periods (1M → since inception)
 * with the ₹1 lakh impact shown underneath the relevant rows. Null rows are
 * hidden entirely (never "—" in the table itself — the row just doesn't
 * appear). A category-rank badge slot is left wired but unpopulated for V1.
 *
 * Used in: src/app/funds/[slug]/page.tsx (Performance Analysis section)
 */

import { formatPercent, formatRupees } from "@/lib/utils/format";

export interface FundReturnsTableProps {
  oneMonth: number | null;
  threeMonth: number | null;
  sixMonth: number | null;
  oneYear: number | null;
  threeYearCagr: number | null;
  fiveYearCagr: number | null;
  sinceInceptionCagr: number | null;

  lakhOneYearValue: number | null;
  lakhThreeYearValue: number | null;
  lakhFiveYearValue: number | null;

  /** Placeholder for V2 — category rank hook, not populated in V1. */
  categoryRank?: { rank: number; total: number } | null;
}

interface Row {
  label: string;
  value: number | null;
  lakhValue?: number | null;
}

export function FundReturnsTable(props: FundReturnsTableProps) {
  const rows: Row[] = [
    { label: "1 Month", value: props.oneMonth },
    { label: "3 Months", value: props.threeMonth },
    { label: "6 Months", value: props.sixMonth },
    { label: "1 Year", value: props.oneYear, lakhValue: props.lakhOneYearValue },
    {
      label: "3 Years (CAGR)",
      value: props.threeYearCagr,
      lakhValue: props.lakhThreeYearValue,
    },
    {
      label: "5 Years (CAGR)",
      value: props.fiveYearCagr,
      lakhValue: props.lakhFiveYearValue,
    },
    { label: "Since Inception (CAGR)", value: props.sinceInceptionCagr },
  ];

  const visibleRows = rows.filter(
    (r) => r.value !== null && r.value !== undefined && isFinite(r.value)
  );

  if (visibleRows.length === 0) {
    return (
      <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
        <p className="text-muted-foreground text-sm">
          Return data not available.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-foreground text-sm font-serif">Returns table</div>
        {props.categoryRank && (
          <span className="bg-blue/15 text-blue text-[10px] font-semibold px-2 py-0.5 rounded-full">
            Rank: {props.categoryRank.rank} of {props.categoryRank.total}
          </span>
        )}
      </div>

      <div className="divide-y divide-surface-light/10">
        {visibleRows.map((row) => {
          const isPositive = (row.value ?? 0) >= 0;
          return (
            <div
              key={row.label}
              className="py-2.5 flex items-start justify-between gap-3"
            >
              <div className="flex-1">
                <div className="text-muted-foreground text-xs">{row.label}</div>
                {row.lakhValue !== null && row.lakhValue !== undefined && (
                  <div className="text-dim text-[11px] mt-0.5 font-tabular">
                    {"\u20B91L \u2192 "}
                    {formatRupees(row.lakhValue)}
                  </div>
                )}
              </div>
              <div
                className={`text-sm font-bold font-tabular shrink-0 ${
                  isPositive ? "text-cgreen" : "text-cred"
                }`}
              >
                {formatPercent(row.value, 2)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
